import {
  Component,
  ChangeDetectorRef,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  inject,
  DestroyRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { PanelsInterfaceComponent } from '../panels-interface.component';
import { Property, Address } from 'app/modules/projects/projects.types';
import { DemographicsDialogComponent } from 'app/shared/modals/demographics/demographics.component';
import { Account, FieldDef } from 'app/core/models/account.types';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { PropertyAddAddressComponent } from 'app/modules/properties/modals/property-add-address/property-add-address.component';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { TableAddressViewOnRendererComponent } from '@shared/components/ts-table-renderers/table-address-renderers/table-address-view-on-renderer.component';
import { TableAddressRendererComponent } from '@shared/components/ts-table-renderers/table-address-renderers/table-address-renderer.component';
import { FeatureFlag } from '@core/enums';
import { NewPermission } from '@core/enums';
import { AccountService } from 'app/core';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { RelatedToType } from '@shared/enums';

@Component({
  selector: 'property-panel-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsMapComponent implements PanelsInterfaceComponent, OnInit {
  @Input() property: Property;
  @Input() account: Account;
  @Input() panelDef: PanelDef<any>;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();
  protected store = inject(Store);

  private _account: Account;

  private readonly destroyRef = inject(DestroyRef);

  titleFieldDef: any;
  panelName: string;
  editMode: boolean;
  changeTitleMode: boolean = false;
  properties: Property[] = [];
  displayColumns: string[];
  lat: number = 40;
  lng: number = -74.5;
  layers = [];
  NewPermission = NewPermission;

  columnDefs: ColumnDef[] = [];
  rowData: any[] = [];
  actions: TableActions[] = [];
  newTable: boolean;

  public selectedLayerPoint: any;
  public selectedMapStyle: string = 'mapbox://styles/mapbox/streets-v9';
  public googleEarthConfig = {
    // Value above sea level. 100,000 meters is the default value.
    cameraAltitude: 100000,
    zoom: 0,
  };
  fieldDefs: FieldDef[];

  constructor(
    private _propertiesService: PropertiesService,
    private _matDialog: MatDialog,
    private _router: Router,
    private _changeDetectorRef: ChangeDetectorRef,
    private _accountService: AccountService,
  ) {
    this.displayColumns = ['address', 'actions'];
  }

  get isAddressHidden(): boolean {
    return this.fieldDefs.find(
      (field) => field.name === 'address_string' && field.related_to_type === RelatedToType.Property,
    )?.meta?.value_hidden;
  }

  ngOnInit() {
    this.fieldDefs = this.data.fieldDefs;
    this.setupStyle();

    this.titleFieldDef = {
      name: 'panel_name',
      label: this.panelDef.title,
      type: 'text',
    };

    this._propertiesService.property$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((property: Property) => {
      if (this.property) {
        this.properties = [];
        this.property = property;
        this.fillProperties();
      }

      this._changeDetectorRef.markForCheck();
    });

    this.checkTable();
    if (this.newTable) {
      this.setColDefs();
    }
  }

  setupStyle() {
    let mapStyle = localStorage.getItem('mapStyle');
    if (mapStyle) {
      try {
        this.selectedMapStyle = mapStyle;
      } catch (e) {
        this.selectedMapStyle = 'mapbox://styles/mapbox/streets-v11';
      }
    } else {
      this.selectedMapStyle = 'mapbox://styles/mapbox/streets-v11';
    }
  }

  changeMapStyle(style) {
    this.selectedMapStyle = style;
    localStorage.setItem('mapStyle', style);
  }

  openDemographicDialog(property): void {
    this._matDialog.open(DemographicsDialogComponent, {
      width: '50vw',
      data: {
        project: this.property,
        data: this.data,
        property: property,
      },
    });
  }

  update(params) {
    let currentKey = Object.keys(params)[0];
    if (currentKey === 'panel_name') {
      this.panelName = params.panel_name;
    }
  }

  saveLayout(): void {
    if (this.panelName) {
      this.panelDef.title = this.panelName;
    }
    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  toggleChangeTitleMode() {
    this.changeTitleMode = true;
  }

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this.changeTitleMode = false;

    this._changeDetectorRef.markForCheck();
  }

  deleteProperty(property): void {
    if (confirm('Are you sure?')) {
      //this._projectsService.deleteProperty(property.id).subscribe();
    }
  }

  fillProperties() {
    this.properties.push(this.property);
    if (this.property.address && this.property.address.latitude && this.property.address.longitude) {
      this.lat = this.property.address.latitude;
      this.lng = this.property.address.longitude;
    }
  }

  openAddressDialog(address?: Address): void {
    this._matDialog
      .open(PropertyAddAddressComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { data: this.data, property: this.property, address: address },
      })
      .afterClosed()
      .subscribe(() => this._changeDetectorRef.markForCheck());
  }

  onLayerClick($event, layer) {
    this.selectedLayerPoint = {
      properties: $event?.features[0]?.properties,
      lngLat: $event?.lngLat,
    };
  }

  goToResearch(property): void {
    const url = this._router.serializeUrl(
      this._router.createUrlTree(['/research'], {
        queryParams: { lat: property.address.latitude, lng: property.address.longitude },
      }),
    );
    window.open(url, '_blank');
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  setColDefs(): void {
    this.columnDefs = [
      {
        label: 'Address',
        name: 'address',
        type: 'text',
        tsTableOptions: {
          headerName: 'Address',
          field: 'address.address_string',
          autoHeight: true,
          sortable: false,
          cellRenderer: TableAddressRendererComponent,
          cellRendererParams: {
            isAddressHidden: this.isAddressHidden,
          },
        },
        meta: {
          readonly: true,
        },
      },
      {
        label: '',
        name: 'actions',
        type: 'text',
        tsTableOptions: {
          width: 30,
          resizable: false,
          pinned: 'right',
          cellRenderer: TableAddressViewOnRendererComponent,
          cellRendererParams: {
            googleEarthConfig: this.googleEarthConfig,
          },
          cellStyle: {
            overflow: 'visible',
            'text-overflow': 'clip',
            'white-space': 'nowrap',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
      },
    ];
    this.setTableActions();
  }

  setTableActions(): void {
    if (!this.isAddressHidden) {
      this.actions = [
        {
          label: 'View Demographics',
          action: (item) => this.openDemographicDialog(item),
          icon: 'supervisor_account',
        },
        {
          label: 'Edit',
          action: (item) => this.openAddressDialog(item.address),
          icon: 'edit',
          id: 'edit',
        },
        {
          label: 'Remove',
          action: (item) => this.deleteProperty(item),
          icon: 'delete',
          id: 'remove',
        },
      ].filter((x) => {
        if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
          if (x.id === 'edit') {
            return this.store.selectSnapshot(
              UserState.hasPermission(NewPermission.PROPERTIES_EDIT, this.property.permissions?.is_editable),
            );
          } else if (x.id === 'remove') {
            return this.store.selectSnapshot(
              UserState.hasPermission(NewPermission.PROPERTIES_DELETE, this.property.permissions?.is_deletable),
            );
          }
        }
        return true;
      });
    }
  }
  checkTable() {
    const account = this._accountService.getCurrentAccount();
    account.features?.includes(FeatureFlag.NEW_TABLE) ? (this.newTable = true) : (this.newTable = false);
  }
}
